import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MicIcon, SquareIcon, Trash2Icon } from 'lucide-react';
interface DictationPadProps {
  transcript: string;
  onTranscriptChange: (value: string) => void;
}

/** Minimal typing for the vendor-prefixed Web Speech API. */
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: any) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: any) => void) | null;
};
function getRecognition(): SpeechRecognitionLike | null {
  const Ctor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!Ctor) return null;
  const recognition: SpeechRecognitionLike = new Ctor();
  recognition.lang = 'da-DK';
  recognition.continuous = true;
  recognition.interimResults = true;
  return recognition;
}
export function DictationPad({
  transcript,
  onTranscriptChange
}: DictationPadProps) {
  const [isListening, setIsListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const committedRef = useRef(transcript);
  useEffect(() => {
    setIsSupported(Boolean((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition));
    return () => recognitionRef.current?.stop();
  }, []);
  const stop = () => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setIsListening(false);
    setInterim('');
  };
  const start = () => {
    const recognition = getRecognition();
    if (!recognition) {
      setIsSupported(false);
      return;
    }
    setError(null);
    committedRef.current = transcript;
    recognitionRef.current = recognition;
    recognition.onresult = (event: any) => {
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
        } else {
          interimText += result[0].transcript;
        }
      }
      if (finalText) {
        committedRef.current = `${committedRef.current} ${finalText}`.trim();
        onTranscriptChange(committedRef.current);
      }
      setInterim(interimText);
    };
    recognition.onerror = (event: any) => {
      setError(event?.error === 'not-allowed' ? 'Mikrofonen blev afvist. Giv adgang i browseren og prøv igen.' : 'Kunne ikke høre dig. Prøv igen.');
      stop();
    };
    recognition.onend = () => {
      setIsListening(false);
      setInterim('');
    };
    recognition.start();
    setIsListening(true);
  };
  return <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-424" data-mp="1r62vdpnw6s8j">
      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-425" data-mp="1r8fmotfjiey9">
        <div className="min-w-0" data-id="element-426" data-mp="3bnvl0te40i60">
          <h2 className="text-base font-semibold text-stone-900" data-id="element-427" data-mp="3bq808yshgefd">
            Fortæl hvad der skal laves
          </h2>
          <p className="mt-0.5 text-sm text-stone-500" data-id="element-428" data-mp="32vtkkv41j1vq">
            Tal som du ville til en kollega: “Fire timer, tredive meter kabel og
            to stikkontakter.”
          </p>
        </div>

        {isSupported && <motion.button whileTap={{
        scale: 0.96
      }} onClick={isListening ? stop : start} aria-pressed={isListening} className={`inline-flex flex-shrink-0 items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${isListening ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-brand-600 text-white hover:bg-brand-700'}`} data-id="element-429" data-mp="2syyp6l32dhlo">
            {isListening ? <>
                <SquareIcon className="h-4 w-4" data-id="element-430" data-mp="d82om2ph1qu7" />
                Stop optagelse
              </> : <>
                <MicIcon className="h-4 w-4" data-id="element-431" data-mp="35zf4pstk4hsu" />
                Indtal opgave
              </>}
          </motion.button>}
      </div>

      {isListening && <p className="mt-4 flex items-center gap-2 text-sm font-medium text-red-600" data-id="element-432" data-mp="2x0a7cptqeui9">
          <span className="relative flex h-2.5 w-2.5" data-id="element-433" data-mp="31y3b7c7kbcfd">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" data-id="element-434" data-mp="2nb0db4fbv1ri" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" data-id="element-435" data-mp="2s1qiohnr74m7" />
          </span>
          Lytter…
        </p>}

      <div className="mt-4" data-id="element-436" data-mp="2ebwttzc90kch">
        <label htmlFor="mester-transcript" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-437" data-mp="2ibni9weayodu">
          Opgavebeskrivelse
        </label>
        <textarea id="mester-transcript" value={interim ? `${transcript} ${interim}`.trim() : transcript} onChange={e => {
        committedRef.current = e.target.value;
        onTranscriptChange(e.target.value);
      }} rows={4} placeholder={isSupported ? 'Indtal opgaven, eller skriv den her…' : 'Skriv opgaven her — din browser understøtter ikke indtaling.'} className="w-full resize-y rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm leading-relaxed text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500" data-id="element-438" data-mp="29apmu2hxjp6v" />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3" data-id="element-439" data-mp="2id36xe1vhexm">
        {transcript && <button onClick={() => {
        committedRef.current = '';
        onTranscriptChange('');
      }} className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 transition-colors hover:text-red-600" data-id="element-440" data-mp="2dfjqq8lm6s31">
            <Trash2Icon className="h-4 w-4" data-id="element-441" data-mp="2uhgtzszvoe8f" />
            Ryd
          </button>}
        {!isSupported && <p className="text-sm text-stone-500" data-id="element-442" data-mp="2zfidgf17ufkf">
            Indtaling virker i Chrome, Edge og Safari.
          </p>}
        {error && <p className="text-sm text-red-600" data-id="element-443" data-mp="3951zftk86pji">{error}</p>}
      </div>
    </div>;
}